import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, MessageSquare, Search, Filter, Clock, CheckCircle, XCircle, AlertCircle, Eye, Mail, Loader2, ChevronDown, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import ThemeToggle from '../components/ThemeToggle';
import axios from 'axios';
import BrandLogo from '../components/BrandLogo';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300', icon: Clock },
  reviewing: { label: 'Reviewing', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300', icon: Eye },
  resolved: { label: 'Resolved', color: 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300', icon: CheckCircle },
  no_action: { label: 'No Action', color: 'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300', icon: XCircle },
  corrected: { label: 'Corrected', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300', icon: RefreshCw }
};

const typeConfig = {
  correction: { label: 'Correction', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300' },
  complaint: { label: 'Complaint', color: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300' },
  appeal: { label: 'Appeal', color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-300' }
};

const AdminComplaintsPage = () => {
  const navigate = useNavigate();
  const [complaints, setComplaints] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    checkAdminAccess();
    loadComplaints();
  }, []);

  const checkAdminAccess = () => {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    if (user && user.is_admin) {
      setIsAdmin(true);
    } else {
      setError('Admin access required');
    }
  };

  const loadComplaints = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API}/complaints/admin/all`, { withCredentials: true });
      setComplaints(response.data.complaints || []);
    } catch (err) {
      console.error('Failed to load complaints:', err);
      setError('Failed to load complaints');
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusUpdate = async (complaintId, newStatus) => {
    try {
      setUpdatingStatus(true);
      await axios.put(`${API}/complaints/admin/${complaintId}/status?status=${newStatus}`, {}, { withCredentials: true });
      setSuccess(`Status updated to ${statusConfig[newStatus]?.label || newStatus}`);
      
      // Update local state
      setComplaints(prev => prev.map(c => 
        c.complaint_id === complaintId ? { ...c, status: newStatus } : c
      ));
      
      if (selectedComplaint?.complaint_id === complaintId) {
        setSelectedComplaint(prev => ({ ...prev, status: newStatus }));
      }
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update status');
      setTimeout(() => setError(''), 3000);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const openDetail = (complaint) => {
    setSelectedComplaint(complaint);
    setShowDetailModal(true);
  };

  const filteredComplaints = complaints.filter(c => {
    const matchesSearch = 
      c.complaint_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.description?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || c.status === statusFilter;
    const matchesType = typeFilter === 'all' || c.complaint_type === typeFilter;
    
    return matchesSearch && matchesStatus && matchesType;
  });

  const stats = {
    total: complaints.length,
    pending: complaints.filter(c => c.status === 'pending').length,
    reviewing: complaints.filter(c => c.status === 'reviewing').length,
    resolved: complaints.filter(c => ['resolved', 'corrected', 'no_action'].includes(c.status)).length
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center">
        <Card className="p-8 dark:bg-slate-800">
          <p className="text-red-600 dark:text-red-400">Admin access required</p>
          <Button onClick={() => navigate('/')} className="mt-4">Go Home</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <BrandLogo size="responsive" data-testid="brand-logo-header" />
            <span className="text-lg sm:text-xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
              KaiCheckIt
            </span>
            <Badge variant="outline" className="ml-2">Admin</Badge>
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <ThemeToggle />
            <Button variant="outline" size="sm" onClick={() => navigate('/')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-6xl mx-auto">
          {/* Title */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                <MessageSquare className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                Complaints Dashboard
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mt-1">Manage corrections, complaints, and appeals</p>
            </div>
            <Button onClick={loadComplaints} variant="outline" disabled={isLoading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-slate-800 dark:text-slate-100">{stats.total}</div>
                <div className="text-sm text-slate-500 dark:text-slate-400">Total</div>
              </CardContent>
            </Card>
            <Card className="dark:bg-slate-800 dark:border-slate-700 border-l-4 border-l-yellow-500">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{stats.pending}</div>
                <div className="text-sm text-slate-500 dark:text-slate-400">Pending</div>
              </CardContent>
            </Card>
            <Card className="dark:bg-slate-800 dark:border-slate-700 border-l-4 border-l-blue-500">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{stats.reviewing}</div>
                <div className="text-sm text-slate-500 dark:text-slate-400">Reviewing</div>
              </CardContent>
            </Card>
            <Card className="dark:bg-slate-800 dark:border-slate-700 border-l-4 border-l-green-500">
              <CardContent className="p-4">
                <div className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.resolved}</div>
                <div className="text-sm text-slate-500 dark:text-slate-400">Resolved</div>
              </CardContent>
            </Card>
          </div>

          {/* Alerts */}
          {error && (
            <Alert className="mb-4 bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800">
              <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400" />
              <AlertDescription className="text-red-800 dark:text-red-300">{error}</AlertDescription>
            </Alert>
          )}
          {success && (
            <Alert className="mb-4 bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800">
              <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
              <AlertDescription className="text-green-800 dark:text-green-300">{success}</AlertDescription>
            </Alert>
          )}

          {/* Filters */}
          <Card className="mb-6 dark:bg-slate-800 dark:border-slate-700">
            <CardContent className="p-4">
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search by ID, name, email, or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10 dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
                    data-testid="complaints-search-input"
                  />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full sm:w-40 dark:bg-slate-900 dark:border-slate-600" data-testid="status-filter">
                    <Filter className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="reviewing">Reviewing</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="corrected">Corrected</SelectItem>
                    <SelectItem value="no_action">No Action</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger className="w-full sm:w-40 dark:bg-slate-900 dark:border-slate-600" data-testid="type-filter">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent className="dark:bg-slate-800 dark:border-slate-600">
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="correction">Correction</SelectItem>
                    <SelectItem value="complaint">Complaint</SelectItem>
                    <SelectItem value="appeal">Appeal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Complaints List */}
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-400" />
            </div>
          ) : filteredComplaints.length === 0 ? (
            <Card className="p-8 text-center dark:bg-slate-800 dark:border-slate-700">
              <MessageSquare className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
              <p className="text-slate-500 dark:text-slate-400">
                {complaints.length === 0 ? 'No complaints submitted yet' : 'No complaints match your filters'}
              </p>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredComplaints.map((complaint) => {
                const StatusIcon = statusConfig[complaint.status]?.icon || Clock;
                return (
                  <Card 
                    key={complaint.complaint_id} 
                    className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => openDetail(complaint)}
                    data-testid={`complaint-card-${complaint.complaint_id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-2">
                            <code className="text-sm font-mono bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded text-slate-700 dark:text-slate-300">
                              {complaint.complaint_id}
                            </code>
                            <Badge className={typeConfig[complaint.complaint_type]?.color || 'bg-slate-100'}>
                              {typeConfig[complaint.complaint_type]?.label || complaint.complaint_type}
                            </Badge>
                            <Badge className={statusConfig[complaint.status]?.color || 'bg-slate-100'}>
                              <StatusIcon className="w-3 h-3 mr-1" />
                              {statusConfig[complaint.status]?.label || complaint.status}
                            </Badge>
                          </div>
                          <p className="text-slate-800 dark:text-slate-200 font-medium truncate">
                            {complaint.name || 'Anonymous'}
                            {complaint.email && (
                              <span className="text-slate-500 dark:text-slate-400 font-normal ml-2">
                                ({complaint.email})
                              </span>
                            )}
                          </p>
                          <p className="text-slate-600 dark:text-slate-400 text-sm line-clamp-2 mt-1">
                            {complaint.description}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 sm:flex-col sm:items-end">
                          <span className="text-xs text-slate-500 dark:text-slate-400">
                            {formatDate(complaint.created_at)}
                          </span>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); openDetail(complaint); }}
                            className="dark:border-slate-600 dark:text-slate-300"
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </main>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto dark:bg-slate-800 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              Complaint Details
            </DialogTitle>
            <DialogDescription className="dark:text-slate-400">
              Review and manage this submission
            </DialogDescription>
          </DialogHeader>
          
          {selectedComplaint && (
            <div className="space-y-6">
              {/* Header Info */}
              <div className="flex flex-wrap items-center gap-2">
                <code className="text-sm font-mono bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded text-slate-700 dark:text-slate-300">
                  {selectedComplaint.complaint_id}
                </code>
                <Badge className={typeConfig[selectedComplaint.complaint_type]?.color}>
                  {typeConfig[selectedComplaint.complaint_type]?.label}
                </Badge>
                <Badge className={statusConfig[selectedComplaint.status]?.color}>
                  {statusConfig[selectedComplaint.status]?.label}
                </Badge>
              </div>

              {/* Submitter Info */}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500 dark:text-slate-400 text-xs uppercase">Submitted By</Label>
                  <p className="text-slate-800 dark:text-slate-200 font-medium">
                    {selectedComplaint.name || 'Anonymous'}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-500 dark:text-slate-400 text-xs uppercase">Contact Email</Label>
                  {selectedComplaint.email ? (
                    <a 
                      href={`mailto:${selectedComplaint.email}`}
                      className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                    >
                      <Mail className="w-4 h-4" />
                      {selectedComplaint.email}
                    </a>
                  ) : (
                    <p className="text-slate-500 dark:text-slate-400">Not provided</p>
                  )}
                </div>
                <div>
                  <Label className="text-slate-500 dark:text-slate-400 text-xs uppercase">Submitted At</Label>
                  <p className="text-slate-800 dark:text-slate-200">
                    {formatDate(selectedComplaint.created_at)}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-500 dark:text-slate-400 text-xs uppercase">Output Reference</Label>
                  <p className="text-slate-800 dark:text-slate-200">
                    {selectedComplaint.output_reference || 'Not specified'}
                  </p>
                </div>
              </div>

              {/* Description */}
              <div>
                <Label className="text-slate-500 dark:text-slate-400 text-xs uppercase">Description</Label>
                <div className="mt-1 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
                  <p className="text-slate-800 dark:text-slate-200 whitespace-pre-wrap">
                    {selectedComplaint.description}
                  </p>
                </div>
              </div>

              {/* Evidence */}
              {selectedComplaint.evidence && (
                <div>
                  <Label className="text-slate-500 dark:text-slate-400 text-xs uppercase">Supporting Evidence</Label>
                  <div className="mt-1 p-4 bg-slate-50 dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-700">
                    <p className="text-slate-800 dark:text-slate-200 whitespace-pre-wrap break-all">
                      {selectedComplaint.evidence}
                    </p>
                  </div>
                </div>
              )}

              {/* Status Update */}
              <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                <Label className="text-slate-700 dark:text-slate-300 font-medium">Update Status</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {Object.entries(statusConfig).map(([key, config]) => {
                    const Icon = config.icon;
                    const isActive = selectedComplaint.status === key;
                    return (
                      <Button
                        key={key}
                        variant={isActive ? "default" : "outline"}
                        size="sm"
                        disabled={updatingStatus || isActive}
                        onClick={() => handleStatusUpdate(selectedComplaint.complaint_id, key)}
                        className={isActive ? '' : 'dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700'}
                        data-testid={`status-btn-${key}`}
                      >
                        <Icon className="w-4 h-4 mr-1" />
                        {config.label}
                      </Button>
                    );
                  })}
                </div>
              </div>

              {/* Reply Action */}
              {selectedComplaint.email && (
                <div className="border-t border-slate-200 dark:border-slate-700 pt-4">
                  <Button 
                    className="w-full"
                    onClick={() => window.location.href = `mailto:${selectedComplaint.email}?subject=Re: ${selectedComplaint.complaint_id} - Your KaiCheckIt Submission`}
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Reply via Email
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminComplaintsPage;
